import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Code2, Globe, ChevronDown, CheckCircle, ArrowLeft, ExternalLink, Calendar, User, Tag, BarChart, Zap } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import Navbar from './Navbar';
import Footer from './Footer';
import ReactMarkdown from 'react-markdown';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--text-main);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const PageContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-muted);
  text-decoration: none;
  margin-bottom: 40px;
  font-weight: 500;
  transition: color 0.3s;
  
  &:hover {
    color: var(--primary);
  }
`;

const ProjectHero = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  margin-bottom: 100px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectInfo = styled.div`
  h1 {
    font-size: clamp(2.5rem, 6vw, 4rem);
    margin-bottom: 24px;
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    line-height: 1.1;
  }
  
  .category {
    color: var(--primary);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 16px;
    display: block;
  }
  
  p.desc {
    font-size: 1.25rem;
    line-height: 1.6;
    color: var(--text-muted);
    margin-bottom: 40px;
  }
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  background: var(--bg-card);
  padding: 30px;
  border-radius: 24px;
  border: 1px solid var(--glass-border);
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(99, 102, 241, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary);
  }
  
  .label {
    display: block;
    font-size: 0.8rem;
    color: var(--text-muted);
    margin-bottom: 4px;
  }
  
  .value {
    font-weight: 600;
    color: var(--text-main);
  }
`;

const ProjectImage = styled(motion.div)`
  width: 100%;
  aspect-ratio: 4/5;
  background-image: url(${props => props.img});
  background-size: cover;
  background-position: center;
  border-radius: 32px;
  border: 1px solid var(--glass-border);
  box-shadow: 0 30px 60px rgba(0,0,0,0.5);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 100px;
`;

const DetailCard = styled(motion.div)`
  background: var(--bg-card);
  padding: 40px;
  border-radius: 24px;
  border: 1px solid var(--glass-border);
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: var(--text-main);
    display: flex;
    align-items: center;
    gap: 15px;
  }
  
  p {
    color: var(--text-muted);
    line-height: 1.8;
  }
`;

const DetailedDescription = styled.section`
  margin-top: 100px;
  background: var(--bg-card);
  padding: 60px;
  border-radius: 32px;
  border: 1px solid var(--glass-border);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 4px;
    background: linear-gradient(to right, var(--primary), var(--secondary));
  }

  h2 {
    font-size: 2.5rem;
    margin-bottom: 40px;
    font-weight: 700;
  }

  .markdown-content {
    color: var(--text-muted);
    font-size: 1.15rem;
    line-height: 1.8;

    p { margin-bottom: 20px; }
    h1, h2, h3 { color: var(--text-main); margin-top: 30px; margin-bottom: 15px; }
    ul, ol { margin-left: 20px; margin-bottom: 20px; }
    li { margin-bottom: 10px; }
    strong { color: var(--text-main); }
    code { 
      background: rgba(255, 255, 255, 0.05);
      padding: 0.2em 0.4em;
      border-radius: 4px;
      font-size: 0.9em;
    }
  }
`;

const LangContainer = styled.div`
  position: relative;
  margin-left: 20px;
`;

const LangButton = styled.button`
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  backdrop-filter: blur(12px);
  overflow: hidden;
`;

const LangItem = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  ${props => props.active && `
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
  `}
`;

const ProjectDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    fetch(`${process.env.REACT_APP_API_URL || ''}/api/projects`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          // Check database projects first
          let found = data.find(p => p.slug === slug);

          if (!found) {
            // Check static projects from i18n
            const staticProjects = t('projects.items', { returnObjects: true });
            if (Array.isArray(staticProjects)) {
              found = staticProjects.find(p => p.slug === slug);
            }
          }

          setProject(found);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [slug]);

  if (loading) {
    return (
      <div style={{ padding: '150px 5%', textAlign: 'center', background: 'var(--bg-dark)', minHeight: '100vh', color: 'white' }}>
        <h1>Loading...</h1>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: '150px 5%', textAlign: 'center', background: 'var(--bg-dark)', minHeight: '100vh', color: 'white' }}>
        <h1>Project Not Found</h1>
        <Link to="/projects" style={{ color: 'var(--primary)', marginTop: '20px', display: 'block' }}>Return to Projects</Link>
      </div>
    );
  }

  return (
    <>
      <Navbar />

      <PageContainer>
        <ContentSection>
          <BackButton to="/projects">
            <ArrowLeft size={18} />
            {t('projects.back')}
          </BackButton>

          <ProjectHero>
            <ProjectInfo>
              <span className="category">{project.category}</span>
              <motion.h1
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                {project.title}
              </motion.h1>
              <motion.p
                className="desc"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {project.desc || (project.description && project.description.substring(0, 160) + '...') || project.fullDesc}
              </motion.p>

              <MetaGrid>
                <MetaItem>
                  <div className="icon"><Calendar size={18} /></div>
                  <div>
                    <span className="label">{t('projects.date')}</span>
                    <span className="value">{project.year || new Date(project.created_at).getFullYear()}</span>
                  </div>
                </MetaItem>
                <MetaItem>
                  <div className="icon"><User size={18} /></div>
                  <div>
                    <span className="label">{t('projects.client')}</span>
                    <span className="value">{project.client || 'Nexova Dev'}</span>
                  </div>
                </MetaItem>
                <MetaItem>
                  <div className="icon"><Tag size={18} /></div>
                  <div>
                    <span className="label">Industry</span>
                    <span className="value">{project.category}</span>
                  </div>
                </MetaItem>
                <MetaItem>
                  <div className="icon"><Globe size={18} /></div>
                  <div>
                    <span className="label">Status</span>
                    <span className="value">Live</span>
                  </div>
                </MetaItem>
              </MetaGrid>
            </ProjectInfo>

            <ProjectImage
              img={project.img}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
            />
          </ProjectHero>

          <DetailGrid>
            {/* Keeping the grid structure if needed for future use, or removing if empty */}
          </DetailGrid>

          <DetailedDescription>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Project Overview
            </motion.h2>
            <motion.div
              className="markdown-content"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <ReactMarkdown>
                {project.description || project.fullDesc || project.desc}
              </ReactMarkdown>
            </motion.div>
          </DetailedDescription>
        </ContentSection>
      </PageContainer>
      <Footer />
    </>
  );
};

export default ProjectDetail;
